import { LayoutGrid } from "lucide-react"
import { type ReactNode, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useInfinitePosts } from "@/entities/post"
import { useDurations } from "@/entities/taxonomy"
import { DurationFilterDialog, type SortOption } from "@/features/filter-posts"
import { PostsSearchBar } from "@/features/search-posts"
import { useUrlState } from "@/shared/hooks/use-url-state"
import { Button } from "@/shared/ui/button"
import { PageTransition } from "@/shared/ui/page-transition"
import { SectionHeading } from "@/shared/ui/section-heading"
import { Skeleton } from "@/shared/ui/skeleton"
import { PostsFeed } from "@/widgets/posts-feed"

type TaxonomyFilterKey = "formatId" | "nicheId" | "topicId"

interface TaxonomyEntity {
	id: string
	name: string
	iconUrl?: string | null
}

interface TaxonomyPostsPageProps {
	entityId: string | undefined
	entity: TaxonomyEntity | null | undefined
	taxonomyLoading: boolean
	filterKey: TaxonomyFilterKey
	notFoundMessage: string
	kindLabel: string
	railTitle: string
	emptyMessage: string
	rail: ReactNode
}

export function TaxonomyPostsPage({
	entityId,
	entity,
	taxonomyLoading,
	filterKey,
	notFoundMessage,
	kindLabel,
	railTitle,
	emptyMessage,
	rail,
}: TaxonomyPostsPageProps) {
	const navigate = useNavigate()
	const searchInputRef = useRef<HTMLInputElement>(null)
	const [searchQuery, setSearchQuery] = useUrlState("q")
	const [searchInput, setSearchInput] = useState(searchQuery)
	const [durationParam, setDurationParam] = useUrlState("duration")
	const selectedDurationIds = useMemo(
		() => (durationParam ? durationParam.split(",") : []),
		[durationParam],
	)
	const setSelectedDurationIds = (value: string[] | ((previous: string[]) => string[])) =>
		setDurationParam((typeof value === "function" ? value(selectedDurationIds) : value).join(","))
	const [sortParam, setSortBy] = useUrlState("sort", "newest")
	const sortBy: SortOption = sortParam === "oldest" ? "oldest" : "newest"

	const {
		data: durations = [],
		isLoading: durationsLoading,
		isError: durationsError,
		error: durationsQueryError,
		refetch: refetchDurations,
	} = useDurations()

	const postsFilter = useMemo(
		() => ({
			isActive: true as const,
			[filterKey]: entityId || undefined,
			sortBy,
			search: searchQuery || undefined,
			durationIds: selectedDurationIds.length > 0 ? selectedDurationIds.join(",") : undefined,
		}),
		[entityId, filterKey, searchQuery, selectedDurationIds, sortBy],
	)

	const {
		data,
		isLoading: postsLoading,
		isError: postsError,
		error: postsQueryError,
		refetch: refetchPosts,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfinitePosts(postsFilter, { enabled: !!entityId })

	const posts = useMemo(() => data?.pages.flat() ?? [], [data])
	const isLoading = postsLoading || taxonomyLoading || durationsLoading
	const isError = postsError || durationsError
	const loadError = postsQueryError ?? durationsQueryError
	const handleRetry = () => {
		void refetchPosts()
		void refetchDurations()
	}

	const activeFilters = [
		searchQuery ? `Поиск: ${searchQuery}` : null,
		...selectedDurationIds.map((id) => durations.find((item) => item.id === id)?.name ?? null),
		sortBy === "oldest" ? "Сначала старые" : null,
	].filter((label): label is string => Boolean(label))

	if (!isLoading && !entity) {
		return (
			<PageTransition>
				<div className="flex min-h-[60vh] items-center justify-center px-4">
					<div className="space-y-4 text-center">
						<p className="text-lg text-muted-foreground">{notFoundMessage}</p>
						<Button onClick={() => navigate("/")}>Вернуться на главную</Button>
					</div>
				</div>
			</PageTransition>
		)
	}

	return (
		<PageTransition>
			<header className="relative isolate -mt-[var(--app-body-padding-top)] pt-[var(--app-body-padding-top)]">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_24%,transparent),transparent_70%)]"
				/>
				<div className="container mx-auto flex max-w-3xl items-center gap-3 px-3 pt-5 pb-6 sm:px-4">
					{taxonomyLoading ? (
						<Skeleton className="size-14 shrink-0 rounded-2xl" />
					) : (
						<span className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-card ring-1 ring-white/10">
							{entity?.iconUrl ? (
								<img src={entity.iconUrl} alt="" className="h-full w-full object-cover" />
							) : (
								<LayoutGrid className="size-6 text-primary" />
							)}
						</span>
					)}
					<div className="min-w-0">
						<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
							{kindLabel}
						</p>
						{entity ? (
							<h1 className="mt-1 text-2xl leading-tight font-bold tracking-tight">
								{entity.name}
							</h1>
						) : (
							<Skeleton className="mt-1 h-7 w-40 rounded-lg" />
						)}
					</div>
				</div>
			</header>

			<div className="container mx-auto max-w-3xl px-3 pb-6 sm:px-4 lg:pb-8">
				<PostsSearchBar
					autoSearch
					value={searchInput}
					onChange={(value) => {
						setSearchInput(value)
						if (!value) setSearchQuery("")
					}}
					onSearch={() => setSearchQuery(searchInput.trim())}
					inputRef={searchInputRef}
					trailing={
						<DurationFilterDialog
							durations={durations}
							selectedDurationIds={selectedDurationIds}
							sortBy={sortBy}
							onDurationToggle={(durationId) =>
								setSelectedDurationIds((prev) =>
									prev.includes(durationId)
										? prev.filter((id) => id !== durationId)
										: [...prev, durationId],
								)
							}
							onSortChange={setSortBy}
							onClearFilters={() => setSelectedDurationIds([])}
						/>
					}
				/>

				<section className="mt-8">
					<SectionHeading title={railTitle} />
					{rail}
				</section>

				{activeFilters.length > 0 && (
					<div className="mt-5 flex flex-wrap items-center gap-2">
						{activeFilters.map((label) => (
							<span key={label} className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium">
								{label}
							</span>
						))}
						<button
							type="button"
							onClick={() => {
								setSearchInput("")
								setSearchQuery("")
								setSelectedDurationIds([])
								setSortBy("newest")
							}}
							className="text-xs font-medium text-primary transition-opacity active:opacity-70"
						>
							Сбросить
						</button>
					</div>
				)}

				<section className="mt-8">
					<SectionHeading title="Материалы" />
					<PostsFeed
						posts={posts}
						isLoading={isLoading}
						isError={isError}
						error={loadError}
						onRetry={handleRetry}
						hasNextPage={hasNextPage}
						isFetchingNextPage={isFetchingNextPage}
						onLoadMore={() => fetchNextPage()}
						emptyMessage={emptyMessage}
					/>
				</section>
			</div>
		</PageTransition>
	)
}
